import React, { useState } from "react";
import {
  X,
  CreditCard,
  Smartphone,
  Building,
  Banknote,
  ChevronRight,
} from "lucide-react";
import MpesaPaymentModal from "./MpesaPaymentModal";
import EquityPaymentModal from "./EquityPaymentModal";

interface PaymentOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  maxAmount: number;
  studentInfo: {
    name: string;
    studentId: string;
  };
  onPaymentSuccess: () => void;
}

const PaymentOptionsModal: React.FC<PaymentOptionsModalProps> = ({
  isOpen,
  onClose,
  maxAmount,
  studentInfo,
  onPaymentSuccess,
}) => {
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [showMpesaModal, setShowMpesaModal] = useState(false);
  const [showEquityModal, setShowEquityModal] = useState(false);

  const paymentMethods = [
    {
      id: "mpesa",
      name: "M-Pesa",
      description: "Pay with M-Pesa mobile money",
      icon: Smartphone,
      iconColor: "text-green-600",
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
      available: false,
    },
    {
      id: "equity",
      name: "Equity Bank",
      description: "Pay with Equity Bank account or cards",
      icon: Building,
      iconColor: "text-red-600",
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
      available: true,
    },
    {
      id: "card",
      name: "Credit/Debit Card",
      description: "Pay with Visa, Mastercard, or other cards",
      icon: CreditCard,
      iconColor: "text-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      available: false, // Coming soon
    },
    {
      id: "bank",
      name: "Bank Transfer",
      description: "Direct bank transfer or online banking",
      icon: Building,
      iconColor: "text-purple-600",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200",
      available: false, // Coming soon
    },
    {
      id: "cash",
      name: "Cash Payment",
      description: "Pay in person at the institution",
      icon: Banknote,
      iconColor: "text-orange-600",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-200",
      available: false, // Admin only
    },
  ];

  const handleMethodSelect = (methodId: string) => {
    if (methodId === "mpesa") {
      setShowMpesaModal(false);
    } else if (methodId === "equity") {
      setShowEquityModal(true);
    } else {
      // For other methods, show coming soon message
      alert("This payment method is coming soon!");
    }
  };

  const handleMpesaClose = () => {
    setShowMpesaModal(false);
  };

  const handleMpesaSuccess = () => {
    setShowMpesaModal(false);
    onPaymentSuccess();
    onClose();
  };

  const handleEquityClose = () => {
    setShowEquityModal(false);
  };

  const handleEquitySuccess = () => {
    setShowEquityModal(false);
    onPaymentSuccess();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-40 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Choose Payment Method
              </h3>
              <p className="text-sm text-gray-500">
                Select how you'd like to pay
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Student Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">
                    {studentInfo.name}
                  </p>
                  <p className="text-sm text-gray-600">
                    ID: {studentInfo.studentId}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Outstanding Balance</p>
                  <p className="font-semibold text-gray-900">
                    KSh {maxAmount.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Payment Methods */}
            <div className="space-y-3">
              {paymentMethods.map((method) => {
                const IconComponent = method.icon;
                return (
                  <button
                    key={method.id}
                    onClick={() => handleMethodSelect(method.id)}
                    disabled={!method.available}
                    className={`w-full p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                      method.available
                        ? `${method.borderColor} ${method.bgColor} hover:shadow-md hover:scale-[1.02] cursor-pointer`
                        : "border-gray-200 bg-gray-50 cursor-not-allowed opacity-60"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div
                          className={`w-12 h-12 rounded-full ${method.bgColor} flex items-center justify-center`}
                        >
                          <IconComponent
                            className={`w-6 h-6 ${method.iconColor}`}
                          />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {method.name}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {method.description}
                          </p>
                          {!method.available && method.id !== "mpesa" && (
                            <span className="text-xs text-gray-500 font-medium">
                              Coming Soon
                            </span>
                          )}
                          {!method.available && method.id === "cash" && (
                            <span className="text-xs text-gray-500 font-medium">
                              Contact Admin
                            </span>
                          )}
                        </div>
                      </div>
                      {method.available && (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Security Note */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <span className="font-medium">🔒 Secure Payment:</span> All
                payments are encrypted and secure. You will receive a
                confirmation message once payment is completed.
              </p>
            </div>

            {/* Help Text */}
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-500">
                Need help? Contact the finance office for assistance
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Mpesa Modal */}
      <MpesaPaymentModal
        isOpen={showMpesaModal}
        onClose={handleMpesaClose}
        maxAmount={maxAmount}
        studentInfo={studentInfo}
        onPaymentSuccess={handleMpesaSuccess}
      />

      {/* Equity Modal */}
      <EquityPaymentModal
        isOpen={showEquityModal}
        onClose={handleEquityClose}
        maxAmount={maxAmount}
        studentInfo={studentInfo}
        onPaymentSuccess={handleEquitySuccess}
      />
    </>
  );
};

export default PaymentOptionsModal;
